import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Typography, Box } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { IContest } from '../../types/global/helper';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';

export interface ITrendingCardProps {
    trendingItem: IContest;
    onClickTrendingCard?: (data: IContest) => void;
}

const EsTrendingCard: React.FC<ITrendingCardProps> = props => {
    const { trendingItem, onClickTrendingCard } = props;
    const { themeStyle } = useThemeContext();
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [showDescriptionToggle, setShowDescriptionToggle] = useState(false);
    const descriptionRef = useRef<HTMLParagraphElement | null>(null);
    const formatContestPhase = (phase?: string) => {
        if (!phase) return 'Active';
        return phase.replace(/_/g, ' ').replace(/\b\w/g, s => s.toUpperCase());
    };

    const getClosingLabel = (expirationDate?: string) => {
        if (!expirationDate) return 'No end date';
        const now = new Date();
        const end = new Date(expirationDate);
        const diffMs = end.getTime() - now.getTime();
        const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (days < 0) return 'Ended';
        if (days === 0) return 'Ends today';
        if (days === 1) return 'Ends in 1 day';
        return `Ends in ${days} days`;
    };
    const plainDescription = useMemo(
        () =>
            (trendingItem.description || '')
                .replace(/<[^>]*>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim(),
        [trendingItem.description],
    );
    useLayoutEffect(() => {
        const el = descriptionRef.current;
        if (!el) return;
        if (isDescriptionExpanded) {
            setShowDescriptionToggle(true);
            return;
        }
        // Detect whether collapsed text exceeds 4 lines.
        setShowDescriptionToggle(el.scrollHeight > el.clientHeight + 1);
    }, [plainDescription, isDescriptionExpanded]);

    return (
        <Box
            onClick={() => onClickTrendingCard && onClickTrendingCard(trendingItem)}
            sx={{
                width: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: theme[themeStyle].deep_grey,
                boxShadow: '0 24px 40px rgba(0,0,0,0.35)',
                cursor: 'pointer',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 30px 48px rgba(0,0,0,0.45)',
                },
                '&:hover .campaign-cover': {
                    transform: 'scale(1.08)',
                },
            }}>
            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '16 / 9',
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                <CustomLazyLoadImage
                    effect="blur"
                    src={trendingItem.feature_image}
                    alt={`${trendingItem.title}_image`}
                    showGradient
                    width={'100%'}
                    height={'100%'}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        objectFit: 'cover',
                        width: '100%',
                        height: '100%',
                        zIndex: 0,
                        transition: 'transform 0.65s ease',
                    }}
                    className="campaign-cover"
                />
            </Box>

            <Box sx={{ p: 2.25, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.2,
                        flexWrap: 'nowrap',
                    }}>
                    <Typography
                        sx={{
                            px: 1.6,
                            py: 0.55,
                            borderRadius: 999,
                            border: `1px solid ${theme[themeStyle].highlight}`,
                            color: theme[themeStyle].highlight,
                            fontSize: 10,
                            letterSpacing: '0.11em',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                        }}>
                        {formatContestPhase(trendingItem.contest_phase)}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: theme[themeStyle].lightWhite,
                            opacity: 0.85,
                            whiteSpace: 'nowrap',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}>
                        {getClosingLabel(trendingItem.expiration_date)}
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        fontSize: { xs: 22, sm: 24 },
                        lineHeight: 1.1,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: theme[themeStyle].filters_white,
                        letterSpacing: '-0.02em',
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                    }}>
                    {trendingItem.title}
                </Typography>

                <Typography
                    ref={descriptionRef}
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: theme[themeStyle].lightWhite,
                        fontSize: 13,
                        lineHeight: 1.55,
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: isDescriptionExpanded ? 'unset' : 4,
                    }}>
                    {plainDescription}
                </Typography>
                {showDescriptionToggle && (
                    <Typography
                        onClick={event => {
                            event.stopPropagation();
                            setIsDescriptionExpanded(prev => !prev);
                        }}
                        sx={{
                            mt: 0.35,
                            fontSize: 12,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: theme[themeStyle].highlight,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { opacity: 0.85 },
                        }}>
                        {isDescriptionExpanded ? 'View less' : 'View more'}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export { EsTrendingCard };
